import { Core } from 'chessboard/core/config/types';
import type { Game } from 'chessboard/model/game/types';
import { createSounds } from 'chessboard/plugins/sounds/create-sounds';
import { Sounds } from 'chessboard/plugins/sounds/config/types';
import { getSoundType } from 'chessboard/plugins/sounds/get-sound-type';
import { isNot } from 'utils/app/model/is-not';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import deepEqual from 'fast-deep-equal';
import type { ThemeAssets } from 'chessboard/config/theme-assets';

/**
 * Create sounds and play them in response to position changes on the board.
 */
export function createSoundsPlugin(soundsInstance?: Sounds.Model): Plugins.Model {
  let sounds: Sounds.Model | undefined = soundsInstance;
  let soundAssetsState: ThemeAssets.SoundAssets | undefined;

  return {
    api: () => ({
      getSoundsInstance: () => sounds,
    }),
    match: [{ condition: isNot(Core.UserEvents.Pointermove), handler: runPlaySoundPlugin }],
    name: Sounds.Plugin.Name,
    create,
  };

  /**
   * Create
   */
  function create(context: Core.Context) {
    soundAssetsState = context.options.themeAssets?.sound.assets;
    if (!sounds) {
      sounds = createSounds({
        options: context.options,
        soundAssets: soundAssetsState,
      });
    }
  }

  /**
   * Run.
   */
  function runPlaySoundPlugin(event: Core.Event, context: Core.Context) {
    if (!sounds) {
      return;
    }

    const { api, options, state } = context;
    const soundsChanged =
      options.soundTheme !== sounds.getTheme() ||
      !deepEqual(soundAssetsState, context.options.themeAssets?.sound.assets);

    soundAssetsState = context.options.themeAssets?.sound.assets;

    // If we're updating the options, then we need to check for sound theme changes.
    if (event.type === Core.OptionsEvents.Update && soundsChanged) {
      sounds.destroy();

      sounds.load({ theme: options.soundTheme, soundAssets: soundAssetsState });

      return;
    }

    // This is in response to an external request to play a sound.
    if (event.type === Core.SoundEvents.Play) {
      sounds.play(event.data.sound);

      state.set({ soundPlayed: event.data.sound });

      return;
    }

    // Only play a sound if it is a move event from the API
    if (!options.playSounds || !event.data?.plyDiff) {
      return;
    }

    let lastMove = api.getSelectedNode();

    /**
     * When the function call is caused by MoveBackward event,
     * move object should be taken from event object
     */
    if (event.type === Core.APIEvents.MoveBackward) {
      lastMove = event.data.move;
    }

    if (!lastMove) {
      return;
    }

    const playingAs = api.getPlayingAs();
    const isOpponentMove = Boolean(playingAs && playingAs !== lastMove.color);
    const type = getSoundType(<Game.Move.Node>lastMove, isOpponentMove);

    if (type) {
      sounds.play(type);

      state.set({ soundPlayed: type });
    }
  }
}
